
import { useEffect, useMemo, useState } from "react";
import { Layout } from "../../components/Layout";
import { PageHeader } from "../../components/PageHeader";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { useLang } from "../../contexts/LangContext";
import { useReceivables, usePayments, useProfile } from "../../hooks/queries";
import { formatMoney } from "../../lib/api";
import { CalendarDays, ChevronLeft, ChevronRight, ArrowDown, ArrowUp, LayoutGrid, List as ListIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { MovementsHeatmap } from "../../components/MovementsHeatmap";
import { LockedSection } from "../../components/LockedSection";
import { tx } from "../../i18n";

/**
 * Heatmap calendar view of upcoming payments + receivables.
 *
 * One month at a time, with month-step navigation. Each day cell shades
 * from cool→warm based on the day's net cash impact (income minus
 * outflow). Clicking a cell shows the list of items below the grid.
 *
 * Pulls existing `/api/receivables` and `/api/payments` data — no new
 * backend route required.
 */
function ymKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function dayKey(d) {
  return `${ymKey(d)}-${String(d.getDate()).padStart(2, "0")}`;
}
function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function daysInMonth(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
}

export default function CalendarPage() {
  const { lang } = useLang();
  const { data: profile } = useProfile();
  const currency = profile?.currency || "USD";
  const { data: receivables = [] } = useReceivables();
  const { data: payments = [] } = usePayments();

  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
  const [selectedKey, setSelectedKey] = useState(null);
  // iter-169zq · Vista lista en mobile.
  // Default to list mode on narrow viewports because the grid forces a
  // 7-column squeeze that hides the per-day amount labels. We honor an
  // explicit toggle so desktop users (or operators on a tablet) can
  // still flip to list if they prefer the chronological read.
  const [viewMode, setViewMode] = useState(() => {
    if (typeof window === "undefined") return "grid";
    return window.matchMedia("(max-width: 639px)").matches ? "list" : "grid";
  });
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia("(max-width: 639px)");
    const onChange = (e) => setViewMode((cur) => {
      // Only auto-switch when the user hasn't manually overridden in
      // this session. We detect "manual override" by storing the last
      // mode and comparing to the breakpoint default.
      const breakpointDefault = e.matches ? "list" : "grid";
      // If the current value equals the *previous* breakpoint default,
      // we assume the user didn't override and we follow the new
      // breakpoint. Otherwise we leave it alone.
      const previousDefault = e.matches ? "grid" : "list";
      return cur === previousDefault ? breakpointDefault : cur;
    });
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Group items by ISO day-key, filtered to the visible month.
  const byDay = useMemo(() => {
    const map = new Map();
    const monthPrefix = ymKey(cursor);
    const upsert = (key, item, isOutflow) => {
      if (!map.has(key)) map.set(key, { incoming: [], outgoing: [], net: 0, total_in: 0, total_out: 0 });
      const slot = map.get(key);
      if (isOutflow) {
        slot.outgoing.push(item);
        slot.total_out += Number(item.amount || 0);
        slot.net -= Number(item.amount || 0);
      } else {
        slot.incoming.push(item);
        slot.total_in += Number(item.amount || 0);
        slot.net += Number(item.amount || 0);
      }
    };
    receivables.forEach((r) => {
      if (r.status === "collected") return;
      const k = (r.expected_date || "").slice(0, 10);
      if (k.startsWith(monthPrefix)) upsert(k, { ...r, _kind: "receivable" }, false);
    });
    payments.forEach((p) => {
      if (p.status === "paid") return;
      const k = (p.due_date || "").slice(0, 10);
      if (k.startsWith(monthPrefix)) upsert(k, { ...p, _kind: "payment" }, true);
    });
    return map;
  }, [receivables, payments, cursor]);

  // Build the grid cells: leading blanks + days + trailing blanks for a 6x7 grid.
  const cells = useMemo(() => {
    const first = startOfMonth(cursor);
    const lead = first.getDay(); // 0 = Sunday
    const total = daysInMonth(cursor);
    const arr = [];
    for (let i = 0; i < lead; i++) arr.push(null);
    for (let day = 1; day <= total; day++) {
      const d = new Date(cursor.getFullYear(), cursor.getMonth(), day);
      arr.push(d);
    }
    while (arr.length % 7 !== 0) arr.push(null);
    return arr;
  }, [cursor]);

  // Compute relative shade for net amounts (heatmap).
  const maxAbs = useMemo(() => {
    let max = 0;
    byDay.forEach((v) => {
      max = Math.max(max, Math.abs(v.net));
    });
    return max || 1;
  }, [byDay]);

  const monthLabel = cursor.toLocaleDateString(tx(lang, undefined, "es-CO", "pt-BR"), {
    month: "long", year: "numeric",
  });
  const weekDayLabels = tx(lang,
    ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
    ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"],
    ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"],
  );

  // Aggregate totals for the visible month.
  const monthlyTotals = useMemo(() => {
    let income = 0, outflow = 0;
    byDay.forEach((v) => {
      income += v.total_in;
      outflow += v.total_out;
    });
    return { income, outflow, net: income - outflow };
  }, [byDay]);

  const selectedSlot = selectedKey ? byDay.get(selectedKey) : null;

  const today = new Date();
  const todayKey = dayKey(today);

  return (
    <Layout>
      <div className="max-w-5xl mx-auto fade-up space-y-6" data-testid="calendar-page">
        <PageHeader
          eyebrow={tx(lang, "Time view", "Vista temporal", "Vista temporal")}
          title={tx(lang, "Calendar", "Calendario", "Calendario")}
          subtitle={tx(lang, "Heatmap of pending receivables and payments. Green = inflow, red = outflow. Click a day for detail.", "Mapa de calor de cobros y pagos pendientes. Verde = entra, rojo = sale. Clic en un día para ver el detalle.", "Mapa de calor de cobros y pagos pendientes. Verde = entra, rojo = sale. Clic en un día para ver el detalle.")}
          testid="calendar-header"
        />
        <div className="space-y-6">
        {/* Month nav + totals */}
        <Card className="lf-surface lf-surface-hover p-5">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Button
                data-testid="cal-prev"
                size="icon"
                variant="ghost"
                onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="font-display text-lg font-bold text-slate-900 capitalize" data-testid="cal-month-label">
                {monthLabel}
              </span>
              <Button
                data-testid="cal-next"
                size="icon"
                variant="ghost"
                onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button
                data-testid="cal-today"
                size="sm"
                variant="outline"
                className="ml-2"
                onClick={() => { setCursor(startOfMonth(new Date())); setSelectedKey(null); }}
              >
                {tx(lang, "Today", "Hoy", "Hoy")}
              </Button>
            </div>
            <div className="flex items-center gap-4 text-xs">
              <div className="text-emerald-700 inline-flex items-center gap-1">
                <ArrowDown className="h-3 w-3" /> {formatMoney(monthlyTotals.income, currency)}
              </div>
              <div className="text-red-700 inline-flex items-center gap-1">
                <ArrowUp className="h-3 w-3" /> {formatMoney(monthlyTotals.outflow, currency)}
              </div>
              <div className={`font-bold ${monthlyTotals.net >= 0 ? "text-emerald-700" : "text-red-700"}`} data-testid="cal-month-net">
                {(monthlyTotals.net >= 0 ? "+" : "") + formatMoney(monthlyTotals.net, currency)}
              </div>
            </div>
          </div>

          {/* View toggle (grid vs list) — list is the default on mobile. */}
          <div
            className="inline-flex items-center bg-slate-100 rounded-xl p-1 mt-3"
            data-testid="cal-view-toggle"
          >
            <button /* design-system: ALLOW legacy-todo */
              type="button"
              onClick={() => setViewMode("grid")}
              data-testid="cal-view-grid"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                viewMode === "grid" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <LayoutGrid className="h-3.5 w-3.5" />
              {tx(lang, "Grid", "Grilla", "Grilla")}
            </button>
            <button /* design-system: ALLOW legacy-todo */
              type="button"
              onClick={() => setViewMode("list")}
              data-testid="cal-view-list"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                viewMode === "list" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <ListIcon className="h-3.5 w-3.5" />
              {tx(lang, "List", "Lista", "Lista")}
            </button>
          </div>

          {/* Grid */}
          {viewMode === "grid" ? (
          <div className="mt-4">
            <div className="grid grid-cols-7 gap-1 text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
              {weekDayLabels.map((d) => (
                <div key={d} className="text-center py-1">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {cells.map((d, idx) => {
                if (!d) return <div key={`blank-${idx}`} className="aspect-square" />;
                const k = dayKey(d);
                const slot = byDay.get(k);
                const isToday = k === todayKey;
                const isSelected = k === selectedKey;
                let bg = "bg-white";
                let netLabel = null;
                if (slot) {
                  const ratio = Math.abs(slot.net) / maxAbs;
                  const intensity = Math.min(1, ratio);
                  // Tailwind-friendly tints
                  if (slot.net > 0) {
                    bg = intensity > 0.66 ? "bg-emerald-300" : intensity > 0.33 ? "bg-emerald-200" : "bg-emerald-100";
                  } else if (slot.net < 0) {
                    bg = intensity > 0.66 ? "bg-red-300" : intensity > 0.33 ? "bg-red-200" : "bg-red-100";
                  } else {
                    bg = "bg-slate-100";
                  }
                  netLabel = slot.net;
                }
                return (
                  <button /* design-system: ALLOW legacy-todo */
                    key={k}
                    type="button"
                    data-testid={`cal-day-${k}`}
                    onClick={() => setSelectedKey(slot ? k : null)}
                    className={`aspect-square rounded-md ${bg} ${isToday ? "ring-2 ring-sky-500" : ""} ${isSelected ? "ring-2 ring-sky-500" : ""} flex flex-col items-center justify-start p-1 transition-all hover:scale-105 disabled:hover:scale-100`}
                    disabled={!slot}
                  >
                    <span className="text-[11px] font-bold text-slate-900">{d.getDate()}</span>
                    {netLabel !== null && (
                      <span className={`text-[9px] mt-auto tabular-nums truncate w-full text-center ${netLabel >= 0 ? "text-emerald-800" : "text-red-800"}`}>
                        {netLabel >= 0 ? "+" : "−"}{formatMoney(Math.abs(netLabel), currency).replace(/[A-Z]{3}/g, "").trim()}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
          ) : (
            <MonthListView
              byDay={byDay}
              lang={lang}
              currency={currency}
              todayKey={todayKey}
              selectedKey={selectedKey}
              setSelectedKey={setSelectedKey}
            />
          )}
        </Card>

        {/* Selected day detail */}
        {selectedSlot && (
          <Card className="p-5 rounded-2xl border-slate-200" data-testid="cal-day-detail">
            <h2 className="font-semibold text-slate-900 mb-3">
              {tx(lang, "Detail ", "Detalle ", "Detalhe ")} {new Date(selectedKey).toLocaleDateString(tx(lang, undefined, "es-CO", "pt-BR"), { weekday: "long", day: "numeric", month: "long" })}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <h3 className="text-xs uppercase tracking-wider text-emerald-700 font-semibold mb-2 inline-flex items-center gap-1">
                  <ArrowDown className="h-3 w-3" /> {tx(lang, "Receivables", "Por cobrar", "Por cobrar")} ({selectedSlot.incoming.length})
                </h3>
                {selectedSlot.incoming.length === 0 ? (
                  <p className="text-xs text-slate-400 italic">—</p>
                ) : (
                  <ul className="space-y-1.5">
                    {selectedSlot.incoming.map((r) => (
                      <li key={r.id} className="text-sm flex justify-between gap-2 p-2 rounded-lg bg-emerald-50">
                        <span className="truncate">{r.client_name}</span>
                        <span className="tabular-nums font-semibold text-emerald-700">{formatMoney(r.amount, r.currency || currency)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <h3 className="text-xs uppercase tracking-wider text-red-700 font-semibold mb-2 inline-flex items-center gap-1">
                  <ArrowUp className="h-3 w-3" /> {tx(lang, "Payments", "Pagos", "Pagos")} ({selectedSlot.outgoing.length})
                </h3>
                {selectedSlot.outgoing.length === 0 ? (
                  <p className="text-xs text-slate-400 italic">—</p>
                ) : (
                  <ul className="space-y-1.5">
                    {selectedSlot.outgoing.map((p) => (
                      <li key={p.id} className="text-sm flex justify-between gap-2 p-2 rounded-lg bg-red-50">
                        <span className="truncate">{p.concept}</span>
                        <span className="tabular-nums font-semibold text-red-700">{formatMoney(p.amount, p.currency || currency)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            <div className="mt-3 flex gap-2 text-xs">
              <Button asChild variant="outline" size="sm">
                <Link to="/receivables">{tx(lang, "View receivables", "Ver por cobrar", "Ver por cobrar")}</Link>
              </Button>
              <Button asChild variant="outline" size="sm">
                <Link to="/payments">{tx(lang, "View payments", "Ver pagos", "Ver pagos")}</Link>
              </Button>
            </div>
          </Card>
        )}

        {byDay.size === 0 && (
          <Card className="p-8 rounded-2xl border-dashed border-slate-200 text-center">
            <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">
              {tx(lang, "Clear month", "Mes despejado", "Mes despejado")}
            </Badge>
            <p className="text-sm text-slate-500 mt-3">
              {tx(lang, "No pending receivables or payments this month.", "No hay cobros ni pagos pendientes este mes.", "No hay cobros ni pagos pendientes este mes.")}
            </p>
          </Card>
        )}
        </div>

        <LockedSection
          featureKey="calendar_heatmap"
          titleEs="Heatmap anual"
          titleEn="Yearly heatmap"
          titlePt="Heatmap anual"
          bullets={[
            { es: "365 días de actividad en una sola vista",
              en: "365 days of activity at a glance",
              pt: "365 dias de atividade em uma vista" },
            { es: "Detecta patrones (días flojos, picos de gasto)",
              en: "Spot patterns (slow days, spend spikes)",
              pt: "Detecte padrões (dias lentos, picos)" },
            { es: "Click en un día para inspeccionar movimientos",
              en: "Click any day to inspect movements",
              pt: "Clique em um dia para inspecionar" },
          ]}
        >
          <MovementsHeatmap lang={lang} />
        </LockedSection>
      </div>
    </Layout>
  );
}


/**
 * <MonthListView /> — iter-169zq.
 *
 * Cronología en lista para mobile: por cada día con movimientos del
 * mes, una fila con la fecha, el neto del día y un mini-resumen de
 * cobros/pagos. Tap en la fila marca el día seleccionado para que el
 * panel de "Detalle" inferior renderice como en el modo grilla.
 */
function MonthListView({ byDay, lang, currency, todayKey, selectedKey, setSelectedKey }) {
  const isEs = lang === "es";
  // Sorted day keys ascending (chronological reading).
  const sortedKeys = Array.from(byDay.keys()).sort();
  if (sortedKeys.length === 0) {
    return (
      <div
        className="mt-4 rounded-xl border border-dashed border-slate-200 p-6 text-center text-sm text-slate-500"
        data-testid="cal-list-empty"
      >
        {isEs ? "Sin movimientos este mes." : "No movements this month."}
      </div>
    );
  }
  return (
    <div
      className="mt-4 rounded-xl border border-slate-200 divide-y divide-slate-100 bg-white"
      data-testid="cal-list-view"
    >
      {sortedKeys.map((k) => {
        const slot = byDay.get(k);
        const isToday = k === todayKey;
        const isSelected = k === selectedKey;
        const d = new Date(k + "T00:00:00");
        const weekday = d.toLocaleDateString(isEs ? "es-CO" : undefined, { weekday: "short" });
        const day = d.getDate();
        const month = d.toLocaleDateString(isEs ? "es-CO" : undefined, { month: "short" });
        return (
          <button /* design-system: ALLOW legacy-todo */
            type="button"
            key={k}
            data-testid={`cal-list-day-${k}`}
            onClick={() => setSelectedKey(isSelected ? null : k)}
            className={`w-full flex items-center gap-3 px-3 py-3 text-left transition-colors ${
              isSelected ? "bg-sky-50" : "hover:bg-slate-50"
            }`}
          >
            <div
              className={`shrink-0 w-12 h-12 rounded-xl flex flex-col items-center justify-center ${
                isToday ? "bg-sky-500 text-white" : "bg-slate-100 text-slate-700"
              }`}
            >
              <span className="text-[9px] uppercase tracking-wider font-semibold">{weekday}</span>
              <span className="text-base font-bold leading-none">{day}</span>
              <span className="text-[9px] uppercase tracking-wider text-current/70 lowercase">{month}</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold text-slate-900">
                  {slot.incoming.length > 0 && (
                    <span className="text-emerald-700">
                      {slot.incoming.length} {isEs ? "cobro" : "receivable"}{slot.incoming.length === 1 ? "" : "s"}
                    </span>
                  )}
                  {slot.incoming.length > 0 && slot.outgoing.length > 0 && (
                    <span className="text-slate-300 mx-1">·</span>
                  )}
                  {slot.outgoing.length > 0 && (
                    <span className="text-red-700">
                      {slot.outgoing.length} {isEs ? "pago" : "payment"}{slot.outgoing.length === 1 ? "" : "s"}
                    </span>
                  )}
                </span>
                <span
                  className={`tabular-nums font-bold text-sm ${slot.net >= 0 ? "text-emerald-700" : "text-red-700"}`}
                >
                  {slot.net >= 0 ? "+" : "−"}
                  {formatMoney(Math.abs(slot.net), currency)}
                </span>
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5 truncate">
                {[...slot.incoming.map((r) => r.client_name), ...slot.outgoing.map((p) => p.concept)]
                  .filter(Boolean)
                  .slice(0, 3)
                  .join(" · ")}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}
